import { signal } from '@preact/signals-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { usePagination, useTable, UseTableCellProps } from 'react-table';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { FormAddProxyProduct } from '../../async_views/form_add_proxy_product';
import { ProductLinksDialog } from '../../async_views/product_links_dialog';
import { ProductRenameDialog } from '../../async_views/product_rename_dialog';
import { getBaseURL } from '../../config/runtime';
import { Product, ProductStyle } from '../../domain/product/product';
import { Products } from '../../domain/product/products';
import { ErrorResponse, isErrorAuth } from '../../error/error';
import { fireUserSignInEvent } from '../../event/user_sign_in_event';
import { fireUserSignOutEvent } from '../../event/user_sign_out_event';
import { isNull } from '../../util/empty_values';
import { getResolveJSON, resolveResp } from '../../util/fetch';
import { Search } from '../form/input/search';
import { IconTick } from '../icon/IconTick';
import { IconEllipsis } from '../icon/icon_ellipsis';
import { IconLink } from '../icon/icon_link';
import { IconLinkedSource } from '../icon/icon_linked_source';
import { IconStyle } from '../icon/icon_style';
import { IconX } from '../icon/icon_x';
import { ImagePreview } from '../image_preview';
import { ContextMenu } from '../menu/context_menu';
import { MessageDeleteCancel } from '../message/message_delete_cancel';
import { MessageYesNo } from '../message/message_yes_no';
import Popup from '../popup/Popup';
import { Pagination } from './Pagination';

export interface Props {
  onSelected: (product: Product) => void;
}

const products = signal<Product[]>([]);

const Styles = styled.div`
  background: #fff;
  margin-right: 2rem;

  table {
    border-spacing: 0;
    font-size: 0.9rem;
    margin-bottom: 1rem;

    thead {
      th {
        font-weight: bold;
        text-align: left;
      }
    }

    tbody {
      tr {
        :nth-child(even) {
          background: #eee;
        }
        &:hover {
          box-shadow: 0 0 0.2rem #bbb;
          /* cursor: pointer; */
          position: relative;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem 0.6rem;
      vertical-align: middle;

      :last-child {
      }
    }
  }
`;

function getDeleteMessage(product: Product | null) {
  if (isNull(product)) {
    return '';
  }

  return product.linked_clones.data.length === 0 ? (
    `Would you like to delete Product ${product?.name}?`
  ) : (
    <div className="mb-6">
      <div className="mb-4">
        Would you like to delete Product{' '}
        <label style={{ fontWeight: 'bold' }}>{product?.name}</label>?
        <br />
        <div className="mt-1.5">
          The following Linked Clones will also be removed:
        </div>
      </div>
      <ul className="mb-2 list-disc pl-5">
        {product.linked_clones.data.map((p) => (
          <li className="mb-2 text-gray-800" key={p}>
            <label className="block text-sm font-semibold">{p}</label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function getCachedProduct(id: string) {
  return products.value.find((p) => p.id === id);
}

function LinkedCloneTooltip(proxyName: string | null) {
  return (
    <div className="py-2 text-gray-800">
      <label className="mb-1 block text-sm font-semibold italic text-[#04B8FF]">
        Linked clone of:
      </label>
      {/* {getCachedProduct(product.proxy)?.name} */}
      <label className="block text-sm font-semibold">{proxyName}</label>
    </div>
  );
}

function LinkedCloneSourceTooltip(clones: string[] | null) {
  return (
    <div className="py-2 text-gray-800" key="proxy">
      <label className="mb-1 block text-sm font-semibold italic text-[#D1811E]">
        Has linked clones:
      </label>
      <ul className="m-0 list-none p-0 py-0 text-left">
        {clones?.map((id) => (
          <li className="py-1 text-sm font-semibold" key={id}>
            {/* {getCachedProduct(id)?.name} */}
            {id}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StyleParentTooltip(styles: ProductStyle[] | null) {
  return (
    <div className="py-2 text-gray-800" key="proxy">
      <label className="mb-1 block text-sm font-semibold italic text-[#D1811E]">
        Has styles:
      </label>
      <ul className="m-0 list-none p-0 py-0 text-left">
        {styles?.map((style) => (
          <li className="pb-1 text-sm font-semibold" key={style.id}>
            {style.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function getIcons(product: Product) {
  const IconProxy = product.proxy ? (
    <>
      <a
        id={`icon-clone-${product.proxy}`}
        data-tooltip-id={`tooltip-${product.proxy}`}
      >
        <IconLink className="mr-2 inline-block h-5 w-5 self-center fill-white stroke-[#04B8FF]" />
      </a>
      <Tooltip
        id={`tooltip-${product.proxy}`}
        opacity={1}
        style={{ background: '#fff', boxShadow: '0 0 .25rem #aaa' }}
        place="top"
        render={() => LinkedCloneTooltip(product.proxy)}
      />
    </>
  ) : null;

  const clones = product.linked_clones.data;
  const IconCloneSrc =
    clones.length > 0 ? (
      <>
        <a
          id={`icon-clone-src-${product.id}`}
          data-tooltip-id={`tooltip-clone-src-${product.id}`}
        >
          <IconLinkedSource className="mr-2 inline-block h-5 w-5 self-center fill-white" />
        </a>
        <Tooltip
          id={`tooltip-clone-src-${product.id}`}
          opacity={1}
          style={{ background: '#fff', boxShadow: '0 0 .25rem #aaa' }}
          place="top"
          render={() => LinkedCloneSourceTooltip(clones)}
        />
      </>
    ) : null;

  const styles = product.styles.data;
  const IconStyles =
    styles && styles.length > 0 ? (
      <>
        <a
          id={`icon-style-parent-${product.id}`}
          data-tooltip-id={`tooltip-style-parent-${product.id}`}
        >
          <IconStyle className="mr-2 inline-block h-5 w-5 self-center fill-white" />
        </a>
        <Tooltip
          id={`tooltip-style-parent-${product.id}`}
          opacity={1}
          style={{ background: '#fff', boxShadow: '0 0 .25rem #aaa' }}
          place="top"
          render={() => StyleParentTooltip(styles)}
        />
      </>
    ) : null;
  return (
    <>
      {IconProxy} {IconCloneSrc} {IconStyles}
    </>
  );
}

interface TableProps {
  columns: any;
  data: Product[];
  setData: (value: React.SetStateAction<Product[]>) => void;
  fetchData: any;
  loading: boolean;
  pageCount: number;
  onRowSelected: any;
}

function Table({
  columns,
  data,
  setData,
  fetchData,
  loading,
  pageCount: controlledPageCount,
  onRowSelected,
}: TableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [originalPageIndex, setOriginalPageIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showFormAddProxyProduct, setShowFormAddProxyProduct] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showProductLinksForm, setShowProductLinksForm] = useState(false);
  const [showProductRenameForm, setShowProductRenameForm] = useState(false);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      // @ts-ignore
      initialState: { pageIndex: 0 }, // Pass our hoisted table state
      manualPagination: true,
      // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    usePagination
  ) as any;

  // Listen for changes in pagination and use the state to fetch our new data
  useEffect(() => {
    fetchData({ pageIndex, pageSize, searchTerm });
  }, [pageIndex, pageSize]);

  useEffect(() => {
    const handler = () => {
      setShowContextMenu(false);
    };

    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const clearSelection = () => {
    setSelectedRow(null);
    onRowSelected(null);
  };
  const onSearch = (term: string, signal: AbortSignal) => {
    clearSelection();
    setSearchTerm(term);
    setOriginalPageIndex(pageIndex);
    gotoPage(0);
    fetchData({ pageSize, pageIndex: 0, searchTerm: term });
  };

  const onSearchClear = () => {
    setSearchTerm('');
    gotoPage(originalPageIndex);
    fetchData({ pageIndex: originalPageIndex, pageSize, searchTerm: '' });
  };

  const onProductUpdate = (product: Product) => {
    const copy = data.slice();
    const i = copy.findIndex((p) => p.id === product.id);
    data.splice(i, 1, product);
    setData(copy);
  };

  const removeProduct = async (product: Product) => {
    try {
      // const resp = await resolveResp({ method: 'DELETE', url: `${getBaseURL()}/v1/client/products/${product.id}` });
      // *Note: Not actually deleting the product, just deactivating it by setting client_active to false.
      const resp = await resolveResp({
        method: 'POST',
        url: `${getBaseURL()}/v1/client/products/${product.id}/deactivate`,
      });
      fetchData({ pageIndex, pageSize, searchTerm });
    } catch (err) {
      // TODO: Notify user of error.
      console.log(err);
    }
  };

  const getContextMenuOptions = (product: Product) => {
    const options = [
      {
        text: 'Show Links',
        onSelect: () => {
          if (selectedProduct) {
            setShowProductLinksForm(true);
          }
        },
      },

      {
        text: 'Rename Product',
        onSelect: () => {
          if (selectedProduct) {
            setShowProductRenameForm(true);
          }
        },
      },
      {
        text: selectedProduct?.visible ? 'Hide' : 'Show',
        onSelect: () => {
          if (selectedProduct) {
            const updatedValue = !selectedProduct.visible;
            selectedProduct.visible = updatedValue;
            onProductUpdate(selectedProduct);
            resolveResp({
              method: 'PUT',
              url: `${getBaseURL()}/v1/client/products/${
                selectedProduct.id
              }/visible?value=${updatedValue}`,
            });
          }
        },
      },
      {
        text: 'Remove',
        onSelect: () => {
          if (selectedProduct) {
            setShowDeleteConfirmation(true);
          }
        },
      },
      {
        text: 'Open',
        onSelect: () =>
          window.open(`${getBaseURL()}/house/${selectedProduct?.id}`),
      },
    ];

    if (!product.proxy) {
      options.push({
        text: 'Add Linked Clone',
        onSelect: () => setShowFormAddProxyProduct(true),
      });
    }

    return options;
  };

  const pageNum = pageIndex + 1;
  const startIndex = pageNum * pageSize - pageSize + 1;
  const endIndex = pageNum * pageSize;
  return (
    <>
      {/* Show Product Links Dialog */}
      {showProductLinksForm && selectedProduct ? (
        <Popup
          onClose={() => {
            setShowProductLinksForm(false);
          }}
          title="Product Links"
        >
          <ProductLinksDialog product={selectedProduct} />
        </Popup>
      ) : null}

      {/* Show Product Rename Dialog */}
      {showProductRenameForm && selectedProduct ? (
        <Popup
          onClose={() => {
            setShowProductRenameForm(false);
          }}
          title="Rename Product"
        >
          <ProductRenameDialog
            product={selectedProduct}
            onSuccess={(name: string) => {
              selectedProduct.name = name;
              onProductUpdate(selectedProduct);
              toast.success('Product renamed.');
            }}
            onClose={() => setShowProductRenameForm(false)}
          />
        </Popup>
      ) : null}

      {showFormAddProxyProduct && selectedProduct ? (
        <Popup
          onClose={() => {
            setShowFormAddProxyProduct(false);
          }}
          title="Add Linked Clone"
        >
          <FormAddProxyProduct
            onProxyProductAdded={() => {
              setShowFormAddProxyProduct(false);
              setOriginalPageIndex(0);
              gotoPage(0);
              fetchData({
                pageIndex: originalPageIndex,
                pageSize,
                searchTerm: '',
              });
            }}
            onProxyProductAddError={(errorResponse: ErrorResponse) => {}}
            proxyProduct={selectedProduct}
          />
        </Popup>
      ) : null}

      {showDeleteConfirmation ? (
        <Popup
          onClose={() => {
            setShowDeleteConfirmation(false);
          }}
          title="Confirm Delete Product"
        >
          <MessageDeleteCancel
            // text={`Are you sure you want to remove the ${selectedProduct?.name} product?`}
            text={getDeleteMessage(selectedProduct)}
            onDelete={() => {
              if (selectedProduct) {
                setShowDeleteConfirmation(false);
                removeProduct(selectedProduct);
                setSelectedProduct(null);
              }
            }}
            onCancel={() => {
              setShowDeleteConfirmation(false);
            }}
          />
        </Popup>
      ) : null}

      <Search
        panelStyle={{ fill: '#555', marginBottom: '1.25rem' }}
        onSearch={onSearch}
        onClear={onSearchClear}
      />

      <table {...getTableProps()}>
        <thead className="table__column-header">
          {headerGroups.map((headerGroup: any) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row: any) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                onClick={() => {
                  setSelectedRow(row.original.id);
                  onRowSelected(row.original);
                }}
                // className={row.original.id === selectedRow ? 'table__row--selected' : ''}
              >
                {row.cells.map((cell: any) => (
                  <td
                    {...cell.getCellProps([
                      {
                        className: cell.column.className,
                        style: cell.column.style,
                      },
                    ])}
                  >
                    {cell.render('Cell')}
                    {cell.column.id === 'actions' ? (
                      <IconEllipsis
                        style={{ verticalAlign: 'middle' }}
                        height="1.2rem"
                        width="1.2rem"
                        onClick={(e: React.MouseEvent<SVGElement>) => {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          setSelectedProduct(row.original);
                          setShowContextMenu(true);
                        }}
                      />
                    ) : null}
                    {cell.column.id === 'actions' &&
                    showContextMenu &&
                    selectedProduct?.id === row.original.id ? (
                      <ContextMenu
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '100%',
                          marginLeft: '1rem',
                          transform: 'translateY(-1rem)',
                        }}
                        items={getContextMenuOptions(row.original)}
                      />
                    ) : null}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <Pagination
        startIndex={startIndex}
        endIndex={endIndex}
        total={pageOptions.length}
        pageSize={pageSize}
        setPageSize={setPageSize}
        hasPrevious={canPreviousPage}
        hasNext={canNextPage}
        previousPage={() => previousPage()}
        nextPage={() => {
          nextPage();
        }}
      />
    </>
  );
}

export const ProductTable = (props: Props) => {
  const { onSelected } = props;

  const columns = [
    {
      Header: '',
      id: 'image',
      Cell: (data: UseTableCellProps<Product>) => (
        <ImagePreview
          url={`${getBaseURL()}/product/${
            data.row.original.id
          }/image?width=300`}
          placeholderSvgXmlData='data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="460.298" height="460.297" fill="%23FFF"><path d="M230.149 120.939L65.986 256.274c0 .191-.048.472-.144.855-.094.38-.144.656-.144.852v137.041c0 4.948 1.809 9.236 5.426 12.847 3.616 3.613 7.898 5.431 12.847 5.431h109.63V303.664h73.097v109.64h109.629c4.948 0 9.236-1.814 12.847-5.435 3.617-3.607 5.432-7.898 5.432-12.847V257.981c0-.76-.104-1.334-.288-1.707L230.149 120.939z"/><path d="M457.122 225.438L394.6 173.476V56.989c0-2.663-.856-4.853-2.574-6.567-1.704-1.712-3.894-2.568-6.563-2.568h-54.816c-2.666 0-4.855.856-6.57 2.568-1.711 1.714-2.566 3.905-2.566 6.567v55.673l-69.662-58.245c-6.084-4.949-13.318-7.423-21.694-7.423-8.375 0-15.608 2.474-21.698 7.423L3.172 225.438c-1.903 1.52-2.946 3.566-3.14 6.136-.193 2.568.472 4.811 1.997 6.713l17.701 21.128c1.525 1.712 3.521 2.759 5.996 3.142 2.285.192 4.57-.476 6.855-1.998L230.149 95.817l197.57 164.741c1.526 1.328 3.521 1.991 5.996 1.991h.858c2.471-.376 4.463-1.43 5.996-3.138l17.703-21.125c1.522-1.906 2.189-4.145 1.991-6.716-.195-2.563-1.242-4.609-3.141-6.132z"/></svg>'
          size="4rem"
        />
      ),
    },
    {
      Header: 'Id',
      accessor: 'id',
    },
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Plan',
      accessor: 'plan_name',
    },
    {
      Header: 'Facade',
      accessor: 'facade_name',
    },
    {
      Header: 'Visible',
      accessor: (data: Product) =>
        data.visible ? (
          <IconTick
            style={{
              height: '1.25rem',
              width: '1.25rem',
              color: '#fff',
              iconBg: '#01cf01',
              iconFg: '#fff',
            }}
          />
        ) : (
          <IconX
            style={{ height: '1.25rem', width: '1.25rem', fill: '#ff3030' }}
          />
        ),
    },
    {
      Header: 'Default orientation',
      accessor: (data: Product) => data.orientation.toLowerCase(),
    },
    {
      Header: 'Location',
      accessor: (data: Product) => data.locations.data.toString(),
    },
    {
      Header: '',
      id: 'proxy',
      Cell: (data: UseTableCellProps<Product>) => getIcons(data.row.original),
    },
    {
      Header: '',
      id: 'actions',
      style: {
        position: 'relative',
      },
    },
    // created on
  ];

  const [data, setData] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const [showError, setShowError] = React.useState(false);

  const fetchData = async ({ pageSize, pageIndex, searchTerm }) => {
    setLoading(true);

    const defaultUrl = `/v1/client/products?page_num=${pageIndex}&page_size=${pageSize}`;
    const url = searchTerm ? `${defaultUrl}&search=${searchTerm}` : defaultUrl;

    const resp = await getResolveJSON<Products | ErrorResponse>(
      getBaseURL() + url
    );
    if (isErrorAuth(resp)) {
      setShowError(true);
      window.rendivo.addPostUserLoginAction(() => {
        fetchData({ pageSize, pageIndex, searchTerm });
      });
    } else {
      products.value = resp.data;
      setData(resp.data);
      setPageCount(resp.total);
      setLoading(false);
    }
  };

  return (
    <>
      {showError ? (
        <Popup
          onClose={() => {
            setShowError(false);
          }}
          title="Session Expired"
        >
          <MessageYesNo
            text="Your session has expired, would you like to re-login to continue?"
            onYes={() => {
              setShowError(false);
              fireUserSignInEvent('SESSION_EXPIRED');
            }}
            onNo={() => {
              setShowError(false);
              fireUserSignOutEvent();
            }}
          />
        </Popup>
      ) : null}

      <Styles>
        <Table
          columns={columns}
          data={data}
          setData={setData}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          onRowSelected={onSelected}
        />
      </Styles>
    </>
  );
};
